import { getIndexerValueForMailboxInfo, type MailboxInfo } from "owa-client-types";
import isCopilotFeatureEnabled from "./isCopilotFeatureEnabled";
import { getApplicationSettings } from "owa-application-settings";
import { logUsage } from "owa-analytics";
import { trace } from "owa-trace";
import { getItem, type LocalStorageKeys, setItem } from "owa-local-storage";
import { isCopilotStoreReady, shouldDisableCopilot } from "owa-copilot-settings-store";
import type { CopilotEnablementDiagnostics } from "owa-copilot-settings-store/lib/utils/enablementCodes/CopilotEnablementTypes";
import { MChatEnablementCodes } from "owa-copilot-settings-store/lib/utils/enablementCodes/MChatEnablementCodes";
import { getLinkedLicensingMailboxInfoOrSelf } from "owa-session-store";
import { isGeneralPreferencesLoaded } from "owa-mail-copilot-settings/lib/selectors/isGeneralPreferencesLoaded";
import { isGlobalFeatureEnabled } from "owa-feature-flags";

/**
 * @param mailboxInfo Mailbox info
 * @returns true if MChat is enabled, false if the feature is disabled, undefined if the feature is in progress or not started
 * based on the feature flags and OCS license check
 */
export default function isMChatEnabled(mailboxInfo: MailboxInfo): boolean | undefined {
    const hasCopilotBusinessChatLicense = isMChatFeatureEnabled(mailboxInfo);
    /* eslint-disable-next-line owa-custom-rules/telemetry-event-name-format -- (https://aka.ms/OWALintWiki)
     * Baseline. DO NOT COPY AND PASTE! Grandfathering existing event names; Devs who touch this file should update Event Names to follow Outlook Telemetry Naming Standards
     *	> Invalid Outlook telemetry EventName "hasCopilotBusinessChatLicense": must start with a capital letter (A–Z). */
    logUsage("hasCopilotBusinessChatLicense", mailboxInfo, { hasCopilotBusinessChatLicense });
    trace.info("License Check Feature enabled and HasCopilotBusinessChatLicense: " +
        hasCopilotBusinessChatLicense);
    return hasCopilotBusinessChatLicense;
}

/**
 * @param mailboxInfo Mailbox info
 * @param skipLanguageCheck  whether to skip language check. Default is true.
 * @param shouldIgnoreUserPreferences whether to ignore user preferences
 * @param diagnostics Optional object to receive diagnostic code when disabled
 * @returns true if the MChat Copilot feature is enabled through OCS, false if the feature is disabled, undefined if the feature is in progress or not started
 */
export function isMChatFeatureEnabled(mailboxInfoToQuery: MailboxInfo, skipLanguageCheck?: boolean, shouldIgnoreUserPreferences?: boolean, diagnostics?: CopilotEnablementDiagnostics): boolean | undefined {
    const mailboxInfo = getLinkedLicensingMailboxInfoOrSelf(mailboxInfoToQuery);
    const copilotStoreReady = isCopilotStoreReady(mailboxInfo);
    const preferencesStoreReady = isGeneralPreferencesLoaded(mailboxInfo);
    const indexer = getIndexerValueForMailboxInfo(mailboxInfo);
    const key: LocalStorageKeys = `isMChatFeatureEnabled${indexer}`;
    
    if (shouldDisableCopilot(mailboxInfo, diagnostics)) {
        return false;
    }
    
    if ((!copilotStoreReady || !preferencesStoreReady) &&
        !isGlobalFeatureEnabled("mon-copilot-bizchat-skip-localstorage") // If this flight is on, skip the local cache entirely (ripcord for local storage issues)
    ) {
        // When shouldIgnoreUserPreferences is true, the cache cannot be trusted: it may have been
        // written by a preference-aware call that returned false because the user disabled Copilot.
        // Return undefined so the caller can retry once the store is ready.
        if (shouldIgnoreUserPreferences) {
            return undefined;
        }
        
        const cachedMChatFeatureEnabled = getItem(window, key);
        const cachedMChatFeatureResult = cachedMChatFeatureEnabled
            ? cachedMChatFeatureEnabled === "true"
            : undefined;
        
        if (diagnostics && !cachedMChatFeatureResult) {
            diagnostics.code =
                cachedMChatFeatureResult === false
                    ? MChatEnablementCodes.MCHAT_CACHED_DISABLED
                    : MChatEnablementCodes.MCHAT_STORE_NOT_READY;
        }
        
        return cachedMChatFeatureResult;
    }
    
    const enableMultiLanguage = skipLanguageCheck ?? getApplicationSettings("Copilot", mailboxInfo).enableMultiLanguage;
    
    const ocsMChatFeatureEnabled = isCopilotFeatureEnabled("MChat", mailboxInfo, enableMultiLanguage, shouldIgnoreUserPreferences, diagnostics);
    
    // Don't cache results from preference-ignoring calls
    if (!shouldIgnoreUserPreferences) {
        setItem(window, key, (ocsMChatFeatureEnabled ?? false).toString());
    }
    
    return ocsMChatFeatureEnabled;
}
